import {
  DEBOUNCE_COMMON_TIMEOUT_IN_MS,
  DEBOUNCE_SEARCH_TIMEOUT_IN_MS,
} from '@/shared/config'
import { type Store, combine, createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'
import { debounce } from 'patronum'

import {
  type GetStatisticsQueryStartCommon,
  getStatisticsAppQuery,
} from '../api'
import {
  STATISTICS_RANGE_FORMATS,
  STATISTICS_RANGE_PRESETS,
} from '../lib/constants'
import { convertDayjsRangeToApiFormat } from '../lib/date'
import {
  getNextStatisticsRange,
  getPrevStatisticsRange,
} from '../lib/range-navigation'
import type { DayjsRange, StatisticsRangePreset } from '../lib/types/common'

// Tab Range
export const rangePresetChanged = createEvent<string>()
export const $rangePreset = createStore<StatisticsRangePreset>('allTime').on(
  rangePresetChanged,
  (_, rangeTab) => rangeTab as StatisticsRangePreset,
)

export const customRangeReset = createEvent()
export const customRangeChanged = createEvent<DayjsRange>()

export const StatisticsPageGate = createGate()
export const $customRange = createStore<DayjsRange | null>(null)
  .on(customRangeChanged, (_, value) => value)
  .reset([customRangeReset, $rangePreset])

export const $range = createStore<DayjsRange>(
  STATISTICS_RANGE_PRESETS['allTime'],
)

export const rangeShiftedPrev = createEvent()
export const rangeShiftedNext = createEvent()

const rangeStringUpdated = sample({
  source: combine([$range, $rangePreset], ([range, preset]) => ({
    range,
    preset,
  })),
  clock: getStatisticsAppQuery.$status,
  filter: (_, status) => status !== 'pending',
  fn: ({ range, preset }) => {
    if (preset === 'day')
      return range.start?.format(STATISTICS_RANGE_FORMATS.month)
    else if (preset === 'week')
      return `${range.start?.format(STATISTICS_RANGE_FORMATS.month)} - ${range.end?.format(STATISTICS_RANGE_FORMATS.month)}`
    else if (preset === 'month')
      return range.start?.format(STATISTICS_RANGE_FORMATS.year)
    else return ''
  },
})
export const $rangeString: Store<string> = createStore('').on(
  rangeStringUpdated,
  (_, newValue) => newValue ?? '',
)

sample({
  source: combine({ rangePreset: $rangePreset, customRange: $customRange }),
  clock: [$rangePreset, $customRange],
  fn: ({ rangePreset, customRange }) => {
    if (customRange) return customRange
    return STATISTICS_RANGE_PRESETS[rangePreset]
  },
  target: $range,
})

sample({
  source: combine({ range: $range, preset: $rangePreset }),
  clock: rangeShiftedPrev,
  fn: ({ range, preset }) => getPrevStatisticsRange(range, preset),
  target: $range,
})

sample({
  source: combine({ range: $range, preset: $rangePreset }),
  clock: rangeShiftedNext,
  fn: ({ range, preset }) => getNextStatisticsRange(range, preset),
  target: $range,
})

export const $rangeAsParams = $range.map(
  (range): GetStatisticsQueryStartCommon => ({
    params: convertDayjsRangeToApiFormat(range),
  }),
)

export const rangeDebounced = debounce({
  source: sample({
    source: $rangeAsParams,
    clock: [$range, StatisticsPageGate.open],
  }),
  timeout: DEBOUNCE_COMMON_TIMEOUT_IN_MS,
})
